import React, { useState } from 'react';
import { X, Download, ChevronDown, ChevronUp } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const NominationDetailsModal = ({
  nomination,
  showDetails,
  setShowDetails,
  statusStyles,
}) => {
  const [expandedScopes, setExpandedScopes] = useState({});
  const [isDocsExpanded, setIsDocsExpanded] = useState(false);

  if (!showDetails || !nomination) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return `${date.toLocaleDateString('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' })} ${date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Brak autoryzacji. Zaloguj się ponownie.');
        return;
      }

      const pdfUrl = nomination.pdf_url || `${API_BASE_URL}/nomination/${nomination.id}/download/`;

      // Pobierz plik z tokenem autoryzacji
      const response = await fetch(pdfUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Pobierz blob i utwórz URL do pobrania
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Utwórz link do pobrania
      const link = document.createElement('a');
      link.href = url;
      link.download = `nominacja_${nomination.id}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Nie udało się pobrać dokumentu PDF.');
    }
  };

  const toggleScope = (scopeId) => {
    setExpandedScopes(prev => ({
      ...prev,
      [scopeId]: !prev[scopeId]
    }));
  };

  const toggleDocsSection = () => {
    setIsDocsExpanded(prev => !prev);
  };

  const inputClasses = "w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-slate-50";
  const labelClasses = "block text-xs font-bold text-slate-500 uppercase mb-2";

  // Filtrujemy kategorie, o które użytkownik się ubiega
  const appliedCategoryIds = nomination.categories || [];
  const getAppliedCategories = (scope) => {
    if (!scope.categories || scope.categories.length === 0) return [];
    return scope.categories.filter(category => appliedCategoryIds.includes(category.id));
  };

  // Zbieramy wszystkie wymagane dokumenty
  const allRequiredDocuments = Array.from(
    new Map(
      [
        ...nomination.work_scope_data.flatMap(scope => scope.required_documents_data || []),
        ...nomination.work_scope_data
          .flatMap(scope => getAppliedCategories(scope))
          .flatMap(category => category.required_documents_data || [])
      ].map(doc => [doc.id, doc])
    ).values()
  );

  // Zbieramy wymagania grupowe tylko z wybranych kategorii
  const allRequiredGroups = Array.from(
    new Map(
      nomination.work_scope_data
        .flatMap(scope => getAppliedCategories(scope))
        .flatMap(category => category.required_doc_groups_category_data || [])
        .map((group, index) => [`group-${index}`, group])
    ).values()
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-brand-50 to-white px-6 py-4 border-b border-brand-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-slate-900">
              Nomination Details: {nomination.nomination_number}
            </h3>
            <span
              className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[nomination.status].color}`}
            >
              {statusStyles[nomination.status].text}
            </span>
          </div>
          <button
            onClick={() => setShowDetails(false)}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">

          {/* Szczegóły */}
          <div className="space-y-5">
            {/* Firma nominująca */}
            <div>
              <label className={labelClasses}>Nominating Company</label>
              <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-700">
                <strong className="text-slate-900">{nomination.company_data.name}</strong> located in{' '}
                {nomination.company_data.address || 'N/A'}, TAX identification no:{' '}
                {nomination.company_data.tax_id || 'N/A'},{' '}
                {nomination.company_representative_data}
              </div>
            </div>

            {/* Informacje o nominowanym */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>Nominated Employee</label>
                <input
                  type="text"
                  value={nomination.user_name || 'N/A'}
                  readOnly
                  className={inputClasses}
                />
              </div>
              <div>
                <label className={labelClasses}>Function</label>
                <input
                  type="text"
                  value={nomination.function || 'N/A'}
                  readOnly
                  className={inputClasses}
                />
              </div>
            </div>

            {/* Okres ważności */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>Valid From</label>
                <input
                  type="text"
                  value={formatDate(nomination.date_start)}
                  readOnly
                  className={inputClasses}
                />
              </div>
              <div>
                <label className={labelClasses}>Valid Until</label>
                <input
                  type="text"
                  value={formatDate(nomination.date_end)}
                  readOnly
                  className={inputClasses}
                />
              </div>
            </div>

            {/* Firma przyjmująca */}
            <div>
              <label className={labelClasses}>Receiving Company</label>
              <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-700">
                <strong className="text-slate-900">{nomination.company_to_data.name}</strong> located in{' '}
                {nomination.company_to_data.address || 'N/A'}, TAX identification no:{' '}
                {nomination.company_to_data.tax_id || 'N/A'}
              </div>
            </div>

            {/* Poziomy autoryzacji */}
            <div className="space-y-2">
              <label className={labelClasses}>Authorization Levels</label>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="space-y-2">
                  {nomination.work_scope_data && nomination.work_scope_data.length > 0 ? (
                    nomination.work_scope_data.map((scope) => (
                      <div key={scope.id}>
                        <div className="flex items-center justify-between">
                          <label className="flex items-start space-x-3 flex-grow">
                            <input
                              type="checkbox"
                              checked={true}
                              disabled
                              className="h-4 w-4 text-brand-500 rounded border-slate-300"
                            />
                            <span
                              onClick={() => toggleScope(scope.id)}
                              className="text-sm text-slate-700 cursor-pointer hover:text-slate-900"
                            >
                              {scope.name}
                            </span>
                          </label>
                          {getAppliedCategories(scope).length > 0 && (
                            <button
                              type="button"
                              onClick={() => toggleScope(scope.id)}
                              className="text-slate-500 hover:text-slate-700"
                            >
                              {expandedScopes[scope.id] ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                        {expandedScopes[scope.id] && getAppliedCategories(scope).length > 0 && (
                          <div className="ml-6 mt-2 space-y-1">
                            {getAppliedCategories(scope).map(category => (
                              <label key={category.id} className="flex items-start space-x-3">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  disabled
                                  className="h-4 w-4 text-brand-500 rounded border-slate-300"
                                />
                                <span className="text-sm text-slate-600">{category.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No authorization levels for this nomination.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Attached Documents */}
            <div className="space-y-2">
              <div className="flex items-center justify-between cursor-pointer" onClick={toggleDocsSection}>
                <label className={labelClasses}>Attached Documents</label>
                {isDocsExpanded ? (
                  <ChevronUp className="h-4 w-4 text-slate-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                )}
              </div>
              {isDocsExpanded && (
                <div className="p-4 bg-slate-50 rounded-xl space-y-4">
                  {/* Pojedyncze dokumenty */}
                  {allRequiredDocuments.length > 0 ? (
                    <div>
                      <h5 className="text-sm font-medium text-slate-700 mb-2">All Required Documents</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {allRequiredDocuments.map(doc => (
                          <div key={doc.id} className="flex items-center">
                            <input
                              type="text"
                              value={doc.name}
                              readOnly
                              className={`${inputClasses} cursor-default`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No required documents for this nomination.</p>
                  )}

                  {/* Wymagania grupowe z kategorii */}
                  {allRequiredGroups.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-slate-700 mb-2">Alternative Group Requirements (One of each)</h5>
                      {allRequiredGroups.map((group, groupIndex) => (
                        <div
                          key={groupIndex}
                          className="mt-2 p-3 border border-slate-200 rounded-lg bg-white"
                        >
                          <p className="text-sm font-medium text-slate-700">Group {groupIndex + 1}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                            {group.map((doc, docIndex) => (
                              <div key={docIndex} className="flex items-center">
                                <input
                                  type="text"
                                  value={doc.name}
                                  readOnly
                                  className={`${inputClasses} cursor-default`}
                                />
                              </div>
                            ))}
                          </div>
                          <p className="text-xs mt-2 text-slate-500">
                            At least one document required from this group
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sekcja informacyjna zależna od statusu */}
            {(nomination.status === 'signed' || nomination.status === 'approved' || nomination.status === 'rejected') && (
              <div className="space-y-2">
                <label className={labelClasses}>Nomination Status Details</label>
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div className="space-y-2">
                    {nomination.date_signed && (
                      <div className="text-sm text-slate-700">
                        <span className="text-xs font-bold text-slate-500 uppercase">Nomination Signed: </span>
                        <span>{formatDateTime(nomination.date_signed)}</span>
                      </div>
                    )}
                    {(nomination.status === 'approved' || nomination.status === 'rejected') && (
                      <div className="text-sm text-slate-700">
                        <span className="text-xs font-bold text-slate-500 uppercase">
                          {nomination.status === 'approved' ? 'Nomination approved by: ' : 'Nomination rejected by: '}
                        </span>
                        <span>{nomination.approver_name || 'N/A'}</span>
                        <span className="text-xs font-bold text-slate-500 uppercase"> on: </span>
                        <span>
                          {nomination.status === 'approved'
                            ? formatDateTime(nomination.date_approved)
                            : formatDateTime(nomination.date_rejected)}
                        </span>
                      </div>
                    )}
                    {nomination.status === 'rejected' && nomination.comment && (
                      <div className="text-sm text-slate-700">
                        <span className="text-xs font-bold text-slate-500 uppercase">Rejection Reason: </span>
                        <span>{nomination.comment}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <button
            onClick={() => setShowDetails(false)}
            className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-2 text-sm font-bold text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors flex items-center shadow-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default NominationDetailsModal;